/**
 * Text direction for caption languages is read from the platform's CLDR data
 * via `Intl.Locale`, so the client owns no list of right-to-left languages that
 * could drift from the languages the server can order. Browsers never infer
 * direction from `lang`, so the caption container needs an explicit `dir`.
 */
type TextInfo = { direction?: "ltr" | "rtl" };

/**
 * `getTextInfo()` is the current accessor; `textInfo` is the earlier property
 * form some browsers still ship instead. Neither is in the TypeScript lib yet.
 */
type LocaleWithTextInfo = Intl.Locale & {
  getTextInfo?: () => TextInfo;
  textInfo?: TextInfo;
};

/**
 * The `dir` for a caption language: "rtl" when the browser reports the language
 * as right-to-left, otherwise undefined.
 *
 * `dir` is only ever added for a known right-to-left language, so left-to-right
 * languages, and every language in a browser that cannot report direction,
 * render with exactly the markup they would have without this attribute.
 */
export function captionDirectionFor(
  code: string | undefined
): "rtl" | undefined {
  if (!code) return undefined;
  let info: TextInfo | undefined;
  try {
    const locale = new Intl.Locale(code) as LocaleWithTextInfo;
    info = locale.getTextInfo?.() ?? locale.textInfo;
  } catch {
    // A malformed code, or Intl.Locale is unavailable; the direction is unknown.
    info = undefined;
  }
  return info?.direction === "rtl" ? "rtl" : undefined;
}
